import type { ConversationMessage } from '@chatbot/shared-types';
import { motion } from 'framer-motion';
import { tildeDe } from '../../lib/estado-de-entrega.ts';
import { ocultarTextoDeReemplazo } from '../../lib/medio-en-burbuja.ts';
import { MedioAdjunto } from './MedioAdjunto.tsx';
import { TildeDeEntrega } from './TildeDeEntrega.tsx';

interface Props {
  message: ConversationMessage;
  isOwnAdmin: boolean;
  /**
   * Por qué canal salió. Decide qué se puede afirmar: Telegram no informa
   * entrega ni lectura, así que ahí nunca hay más de un tilde.
   */
  canal?: 'whatsapp' | 'telegram';
}

export function MessageBubble({ message, isOwnAdmin, canal = 'whatsapp' }: Props) {
  const isUser = message.role === 'user';
  const isAdmin = message.role === 'admin';
  const isBot = message.role === 'assistant';

  // Chat layout: user IZQUIERDA, bot + admin DERECHA. Bot tiene tratamiento
  // visual futurista (gradient + glow) para diferenciar claramente del admin.
  const alignment = isUser ? 'justify-start' : 'justify-end';

  let bubbleClass: string;
  let extraStyle: React.CSSProperties | undefined;
  if (isUser) {
    bubbleClass = 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100';
  } else if (isBot) {
    // Bot futurista: gradient cyan→purple + neon glow + border holográfico
    bubbleClass = 'text-white relative bot-bubble-futuristic';
    extraStyle = {
      background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 60%, #ec4899 100%)',
      boxShadow:
        '0 0 0 1px rgba(139, 92, 246, 0.35), 0 4px 18px -2px rgba(139, 92, 246, 0.4), 0 0 24px -4px rgba(6, 182, 212, 0.35)',
    };
  } else if (isAdmin && isOwnAdmin) {
    bubbleClass = 'bg-primary-600 text-white';
  } else {
    // Admin (otro operador)
    bubbleClass = 'bg-primary-700 text-white opacity-90';
  }

  // Label header
  let label: { text: string; icon: string; futuristic?: boolean } | null = null;
  if (isBot) label = { text: 'AI Assistant', icon: '🤖', futuristic: true };
  else if (isAdmin && isOwnAdmin) label = { text: 'Tú (operador)', icon: '👤' };
  else if (isAdmin && message.adminName) label = { text: message.adminName, icon: '👤' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16 }}
      className={`flex ${alignment} mb-2`}
    >
      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${bubbleClass}`} style={extraStyle}>
        {label && (
          <div
            className={`text-xs mb-1 font-medium flex items-center gap-1.5 ${
              label.futuristic ? 'tracking-wider uppercase' : 'opacity-80'
            }`}
          >
            {label.futuristic ? (
              <motion.span
                aria-hidden
                animate={{ opacity: [1, 0.6, 1] }}
                transition={{ duration: 1.8, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
                className="text-base leading-none"
              >
                {label.icon}
              </motion.span>
            ) : (
              <span aria-hidden>{label.icon}</span>
            )}
            <span className={label.futuristic ? 'font-semibold text-[0.7rem]' : ''}>
              {label.text}
            </span>
            {label.futuristic && (
              <span
                aria-hidden
                className="ml-auto inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_6px_rgba(110,231,183,0.9)]"
              />
            )}
          </div>
        )}
        {/* El texto de reemplazo (`[audio]`) sobra cuando el reproductor está a
            la vista: dice en palabras lo que el control ya muestra. Si el
            archivo no se puede abrir, el texto se queda — es lo único que le
            avisa al operador que la persona mandó algo. */}
        {ocultarTextoDeReemplazo(message) ? null : (
          <div className="whitespace-pre-wrap text-sm">{message.text}</div>
        )}
        {message.medio ? <MedioAdjunto medio={message.medio} id={message.id} /> : null}
        {/* Una reacción vacía es una que quitaron: se deja de dibujar. Ausente y
            vacía se ven igual, y eso está bien — la diferencia importa en el
            dato, no en la pantalla. */}
        {message.reaccion ? (
          <span
            className="inline-block mt-1 rounded-full bg-black/20 px-2 py-0.5 text-sm leading-none"
            title="Reaccionó a este mensaje"
            aria-label={`Reaccionó ${message.reaccion} a este mensaje`}
          >
            {message.reaccion}
          </span>
        ) : null}
        {/* La opacidad va SÓLO en la hora. Estaba en el contenedor, y como el
            tilde tenía la suya propia las dos se multiplicaban: 0,65 × 0,70 =
            0,45 de opacidad a 10px sobre un degradado. El indicador existía en el
            DOM y no comunicaba nada — que es lo mismo que no estar. */}
        <div className="text-[10px] mt-1 text-right flex items-center justify-end gap-1.5">
          <span className="opacity-65">
            {new Date(message.timestamp).toLocaleTimeString('es-CO', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {(() => {
            const t = tildeDe(canal, message.entrega, message.role);
            return t ? <TildeDeEntrega tilde={t} /> : null;
          })()}
        </div>
      </div>
    </motion.div>
  );
}
